import React from 'react';
import { FiCheckCircle, FiFileText, FiRefreshCw, FiX } from 'react-icons/fi';
import { inlineImageUrl } from './inlineImages';
import './InlineImageAttachments.css';

const STAGES = {
  queued: 'Queued',
  reading: 'Reading image',
  preparing: 'Preparing image',
  uploading: 'Uploading',
  publishing: 'Finalizing attachment',
  loading: 'Checking image',
  failed: 'Failed',
};

export function sameInlineAttachment(left, right) {
  const ids = file => [file.contentDocumentId, file.fileId, file.id].filter(Boolean);
  if (ids(left).some(id => ids(right).includes(id))) return true;
  const url = inlineImageUrl(left.contentDownloadUrl || left.downloadUrl);
  return Boolean(url && url === inlineImageUrl(right.contentDownloadUrl || right.downloadUrl));
}

const fileSize = bytes => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const localPreview = upload => Number(upload.bytes) > 0
  && /^image\/(png|jpeg|gif|webp)$/i.test(upload.type || '')
  && /^(blob:|data:image\/(png|jpeg|gif|webp);base64,)/i.test(upload.previewUrl || '')
  ? upload.previewUrl : '';

export default function InlineImageAttachments({ uploads = [], completedFiles = [], visibleFiles = [], disabled = false, onRetry, onRemove }) {
  const completed = completedFiles.filter((file, index) =>
    !visibleFiles.some(visible => sameInlineAttachment(file, visible))
    && !completedFiles.slice(0, index).some(previous => sameInlineAttachment(file, previous)));
  if (!uploads.length && !completed.length) return null;

  return (
    <ul className="inline-image-attachments" aria-label="Pasted attachments">
      {uploads.map(upload => {
        const name = upload.name || 'Pasted image';
        const preview = localPreview(upload);
        const failed = upload.status === 'failed' || Boolean(upload.error);
        const progress = Math.round(Math.min(100, Math.max(0, Number(upload.progress) || 0)));
        return (
          <li className="inline-image-attachments__row" key={upload.id} data-upload-status={failed ? 'failed' : upload.status}>
            <span className="inline-image-attachments__thumbnail">
              {preview ? <img src={preview} alt="" /> : <FiFileText aria-hidden="true" />}
            </span>
            <div className="inline-image-attachments__details">
              <strong className="inline-image-attachments__name">{name}</strong>
              <span className="inline-image-attachments__meta">
                <span role="status">{failed ? 'Failed' : STAGES[upload.status] || 'Preparing image'}</span>
                {fileSize(upload.bytes) && <span>{fileSize(upload.bytes)}</span>}
                <span>{progress}%</span>
              </span>
              <progress max="100" value={progress} aria-label={`Attachment upload progress: ${name}`} />
              {failed && upload.error && <span className="inline-image-attachments__error">{upload.error}</span>}
            </div>
            <div className="inline-image-attachments__actions">
              {failed && <button type="button" disabled={disabled || !onRetry} title={`Retry ${name}`} aria-label={`Retry ${name}`}
                onMouseDown={event => event.preventDefault()} onClick={() => onRetry(upload.id)}><FiRefreshCw aria-hidden="true" /></button>}
              <button type="button" disabled={disabled || !onRemove} title={`Remove ${name}`} aria-label={`Remove ${name}`}
                onMouseDown={event => event.preventDefault()} onClick={() => onRemove(upload.id)}><FiX aria-hidden="true" /></button>
            </div>
          </li>
        );
      })}
      {completed.map(file => {
        const name = file.name || file.fileName || 'Pasted image';
        const url = inlineImageUrl(file.contentDownloadUrl);
        return (
          <li className="inline-image-attachments__row" key={file.contentDocumentId || file.fileId || file.id || url} data-upload-status="complete">
            <span className="inline-image-attachments__thumbnail">
              {url ? <img src={url} alt="" loading="lazy" referrerPolicy="no-referrer" /> : <FiFileText aria-hidden="true" />}
            </span>
            <div className="inline-image-attachments__details">
              {url ? <a className="inline-image-attachments__name" href={url} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer">{name}</a>
                : <strong className="inline-image-attachments__name">{name}</strong>}
              <span className="inline-image-attachments__meta"><FiCheckCircle aria-hidden="true" /><span>Attached</span></span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}
